import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Bundle } from '@/features/admin/bundle/Bundle'
import { type RefreshBundlesFn } from '@/features/admin/bundle/BundleSearchViewModel'
import { type BundleStore } from '@/features/admin/bundle/BundleStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'

/**
 * View model for managing the Unarchive dialog.
 */
export class UnarchiveBundleDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor bundle: Bundle = Bundle.none

  constructor(
    private readonly _bundleStore: BundleStore,
    private readonly _refreshBundles: RefreshBundlesFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Asks the user to unarchive the bundle.
   *
   * @returns Whether the bundle was unarchived.
   */
  async prompt(bundle: Bundle, signal?: AbortSignal) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.bundle = bundle
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  /**
   * Unarchives the bundle.
   */
  unarchive = task.resolved(async () => {
    await this._bundleStore
      .unarchive(this.bundle.id)
      .catch(this._createAlertErrorHandler('Unarchiving the bundle failed'))

    await this._refreshBundles()

    this._newAlert()
      .withState('success')
      .saying('Success', 'Bundle unarchived.')
      .autoDismiss()
      .show()
    this._prompt.respond(true)
    this.modal.close()
  })
}
